import type { ApiErrorConfig, ApiErrorType } from '~/types'

export default function useApiError() {
  const { t } = useI18n()

  const ErrorStatus: Record<ApiErrorType, ApiErrorConfig> = {
    NOT_FOUND: {
      statusCode: 404,
      statusMessage: t('app.error.notFound.statusMessage'),
      message: t('app.error.notFound.message'),
      fatal: true,
      retryable: false
    },
    SERVER_ERROR: {
      statusCode: 500,
      statusMessage: t('app.error.server.statusMessage'),
      message: t('app.error.server.message'),
      fatal: true,
      retryable: false
    },
    API_UNAVAILABLE: {
      statusCode: 503,
      statusMessage: t('app.error.apiUnavailable.statusMessage'),
      message: t('app.error.apiUnavailable.message'),
      fatal: true,
      retryable: true
    },
    API_RATE_LIMIT: {
      statusCode: 429,
      statusMessage: t('app.error.apiRateLimit.statusMessage'),
      message: t('app.error.apiRateLimit.message'),
      fatal: false,
      retryable: true
    }
  } as const

  /**
   * Throws a Nuxt error with predefined configuration & i18n message
   */
  const throwError = (status: ApiErrorType, customMessage?: string) => {
    const error = ErrorStatus[status]
    throw createError({
      ...error,
      message: customMessage || error.message
    })
  }

  /**
   * Handles API response errors
   */
  const handleApiError = (error: any, entityName?: string) => {
    console.error(`Failed to fetch ${entityName || 'data'}:`, error)

    switch (error?.response?.status) {
      case 404:
        throwError('NOT_FOUND')
        break
      case 429:
        throwError('API_RATE_LIMIT')
        break
      case 500:
        throwError('SERVER_ERROR')
        break
      default:
        throwError('API_UNAVAILABLE', error?.message)
    }
  }

  return {
    throwError,
    handleApiError
  }
}
